import { Component, Input, OnInit, Output, EventEmitter } from "@angular/core";
import { WaterwiseService } from "../../../routes/services/waterwise.service";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { StorageService } from "../../../routes/services/storage.service";
import { ClientsService } from "../../../routes/services/clients.service";
import { GlobalFunction } from "../../../routes/services/globalFuntion";
import { transparent, waterwise_icons } from "../../../routes/services/icons";
import { FarmService } from "../../../routes/services/farm.service";
import { PondService } from "../../../routes/services/pond.service";
import { WaterwiseBrandsService } from "../../../routes/Admin/waterwises/services/waterwise-brands.service";
import { WaterwiseBrand } from "../../../routes/Admin/waterwises/interfaces/waterwise-brand.interfaces";

@Component({
  selector: "app-waterwise-form",
  templateUrl: "./waterwise-form.component.html",
  styleUrls: ["./waterwise-form.component.scss"]
})
export class FormWaterwiseComponent implements OnInit {
  @Input("id") id: number | null = null;
  @Input() lat: any = 25.7873277;
  @Input() lng: any = -109.0098591;
  @Input() zoom: any = 15;
  @Input() selectedItems: [] = [];
  @Input() massiveUpdate: boolean = false;

  viewMap = false;
  formWaterwise: FormGroup;

  filteredClients = [];
  clients = [];
  farms = [];
  ponds = [];

  farm: any = null;

  transparent = transparent;
  selectMarker = {
    lat: 0,
    lng: 0
  };

  loadingClient = false;
  loadingFarms = false;
  loadingPonds = false;
  loadingBrands = false;

  brands: WaterwiseBrand[] = [];

  meta_data = {};
  generated_meta_data = {};

  @Output("petition") submit_petition = new EventEmitter();
  loading_petition: boolean = false;

  constructor(
    public waterwiseService: WaterwiseService,
    public storageService: StorageService,
    public globalFunction: GlobalFunction,
    public clientService: ClientsService,
    public farmService: FarmService,
    public pondService: PondService,
    public waterwiseBrandsService: WaterwiseBrandsService
  ) {
    this.formWaterwise = new FormGroup({
      pond_id: new FormControl("", Validators.required),
      name: new FormControl("", Validators.required),
      mac: new FormControl("", globalFunction.validateSoloAdmin() ? Validators.required : null),
      filteredBusinessName: new FormControl(""),
      device_type: new FormControl(""),
      brand: new FormControl(""),
      client_id: new FormControl(""),
      farm_id: new FormControl(""),
      lat: new FormControl(""),
      lng: new FormControl(""),
      warranty_months: new FormControl(""),
      warranty_start_date: new FormControl("")
    });
  }

  ngOnInit(): void {
    let getQuery = this.storageService.getQueryFilters(this.formWaterwise.value);

    for (const key in getQuery) {
      if (this.formWaterwise.value.hasOwnProperty(key)) {
        this.formWaterwise.controls[key].setValue(getQuery[key]);
      }
    }

    this.getBrands();

    if (!this.id) {
      this.formWaterwise.controls["name"].setValue("");
      this.formWaterwise.controls["mac"].setValue("");
      this.formWaterwise.controls["pond_id"].setValue("");

      if (this.storageService.validateSoloAdmin()) {
        this.getClients();
      }

      getQuery.client_id && this.getFarms();
      getQuery.farm_id && this.getPonds();
    }

    this.id && this.getWaterwise();

    if (this.massiveUpdate && this.selectedItems.length > 0) {
      this.getFarms();
    }
  }

  getWaterwise = async () => {
    this.waterwiseService.getWaterwise(this.id).subscribe((resp) => {
      this.meta_data = resp.metadata;

      if (resp.pond) {
        this.formWaterwise.controls["pond_id"].setValue(resp.pond.id);
      }
      if (resp.farm) {
        this.formWaterwise.controls["farm_id"].setValue(resp.farm.id);
      }
      if (resp.client) {
        this.formWaterwise.controls["client_id"].setValue(resp.client.id);
      }
      if (resp.brand) {
        this.formWaterwise.controls["brand"].setValue(resp.brand.id);
      }

      ["name", "mac", "lat", "lng", "device_type"].forEach((prop) => {
        this.formWaterwise.controls[prop].setValue(resp[prop]);
      });
      this.formWaterwise.controls["warranty_months"].setValue(resp.warranty_months ?? "");
      this.formWaterwise.controls["warranty_start_date"].setValue(resp.warranty_start_date ?? "");
      if (!this.globalFunction.validateSoloAdmin()) {
        this.formWaterwise.controls["name"].disable();
        this.formWaterwise.controls["device_type"].disable();
      }

      let lat = resp.lat;
      let lng = resp.lng;

      this.selectMarker = {
        lat,
        lng
      };

      const { farm_id, client_id } = this.formWaterwise.value;
      if (this.storageService.isAdmin()) {
        this.getClients();
      }
      client_id && this.getFarms();
      farm_id && this.getPonds();
    });
  };

  getClients = () => {
    this.loadingClient = true;
    this.clientService.getClients(`?fields=id,name`).subscribe((resp) => {
      this.loadingClient = false;
      this.clients = resp;
      this.filterClientsList();
    });
  };

  cleanClientsFilteredList() {
    this.formWaterwise.controls["filteredBusinessName"].setValue("");
    this.filteredClients = this.clients;
  }

  filterClientsList() {
    const { filteredBusinessName } = this.formWaterwise.value;

    if (filteredBusinessName) {
      this.filteredClients = this.clients.filter((item: any) => {
        return item.name.toLowerCase().indexOf(filteredBusinessName.toLowerCase()) > -1;
      });
    } else {
      this.filteredClients = this.clients;
    }
  }

  getFarms = () => {
    this.loadingFarms = true;
    const { client_id } = this.formWaterwise.value;
    const fields = ["id", "name", "coordinates"];
    const query_client = this.globalFunction.validateSoloAdmin() ? `&client=${client_id}` : "";
    this.farmService.getFarms(`?fields=${fields}${query_client}`).subscribe((resp) => {
      this.loadingFarms = false;
      this.farms = resp;

      const { farm_id } = this.formWaterwise.value;
      this.farm = resp.find((item: any) => Number(item.id) === Number(farm_id)) || null;

      if (this.farm) {
        const { lat, lng } = this.globalFunction.centerPolygon(this.farm.coordinates);
        this.lat = lat;
        this.lng = lng;
      }
    });
  };

  getPonds = () => {
    this.loadingPonds = true;
    const { farm_id } = this.formWaterwise.value;
    this.farm = this.farms.find((item: any) => Number(item.id) === Number(farm_id)) || null;
    if (this.farm && this.farm.coordinates) {
      this.viewMap = true;
      const { lat, lng } = this.globalFunction.centerPolygon(this.farm.coordinates);
      this.lat = lat;
      this.lng = lng;
    }

    const fields = ["id", "name", "coordinates"];
    const { alert_query, name_query } = this.storageService.getQueryFilters({
      alert_query: "",
      name_query: ""
    });
    const alert = alert_query ? `-alert_count,` : "";
    const name = name_query ? `name_advance` : "number";

    const object_query = {
      farm_id,
      fields,
      ordering: alert + name,
      all_records: true
    };
    const query = this.globalFunction.constructQuery(object_query);

    this.pondService.getPonds(query).subscribe((resp: any) => {
      this.loadingPonds = false;
      this.ponds = resp;
    });
  };

  submitForm = () => {
    const {
      name, pond_id, mac, lat, lng, client_id, farm_id, device_type, brand,
      warranty_months, warranty_start_date
    } = this.formWaterwise.value;

    let obj: any = {
      name,
      mac,
      lat,
      lng,
      device_type,
      metadata: this.generated_meta_data
    };

    if (pond_id) {
      obj["pond"] = Number(pond_id);
    }
    if (farm_id) {
      obj["farm"] = Number(farm_id);
    }
    if (this.storageService.isAdmin() && client_id) {
      obj["client"] = Number(client_id);
    }
    if (brand) {
      obj["brand"] = Number(brand);
    }
    if (warranty_months !== "" && warranty_months !== null) {
      obj.warranty_months = Number(warranty_months);
    }
    if (warranty_start_date) {
      obj.warranty_start_date = warranty_start_date;
    }

    this.loading_petition = true;
    if (this.massiveUpdate) {
      this.waterwiseService
        .EditMasiveWaterwises(
          {
            device_type: Number(device_type),
            pond: pond_id ? Number(pond_id) : "",
            farm: farm_id ? Number(farm_id) : "",
            brand: brand ? Number(brand) : "",
            client: this.storageService.isAdmin() && client_id ? Number(client_id) : ""
          },
          `?id_in=${this.selectedItems.map((item: any) => item.id)}`
        )
        .subscribe((resp) => {
          this.submit_petition.emit();
          this.loading_petition = false;
        });
    } else {
      if (this.id) {
        this.waterwiseService.patchWaterwise(this.id, obj).subscribe(
          (resp) => {
            this.submit_petition.emit();
            this.loading_petition = false;
          },
          (error) => {
            this.loading_petition = false;
          }
        );
      } else {
        this.waterwiseService.postWaterwise(obj).subscribe(
          (resp) => {
            this.submit_petition.emit(resp);
            this.loading_petition = false;
          },
          (error) => {
            this.loading_petition = false;
          }
        );
        this.loading_petition = false;
      }
    }
  };

  textMac(value: string) {
    return this.globalFunction.agregarCaracter(value, ":", 2);
  }
  dragMarket(event) {
    if (event.coords) {
      const pond = this.ponds.find((item: any) => {
        return this.globalFunction.insidePolygon(
          event.coords.lat,
          event.coords.lng,
          item.coordinates
        );
      });
      if (pond) {
        this.markerDragEnd(pond, { coords: event.coords });
      } else {
        this.formWaterwise.controls["pond_id"].setValue("");
      }
    }
  }
  clickPolygonPond({ event, value }) {
    const exist = this.ponds.some((item: any) => item.id === value.id);
    if (exist) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      this.formWaterwise.controls["lat"].setValue(lat);
      this.formWaterwise.controls["lng"].setValue(lng);

      this.selectMarker = {
        lat,
        lng
      };
      this.formWaterwise.controls["pond_id"].setValue(value.id);
    }
  }
  changeInputLatLng(type, value) {
    this.selectMarker[type] = value;
  }

  markerDragEnd(pond, { coords: { lat, lng } }) {
    this.formWaterwise.controls["pond_id"].setValue(pond.id);
    this.selectMarker = {
      lat,
      lng
    };
    this.formWaterwise.controls["lat"].setValue(lat);
    this.formWaterwise.controls["lng"].setValue(lng);
  }

  validateIcon() {
    return waterwise_icons.sano;
  }

  deleteLatLng() {
    this.selectMarker = {
      lat: 0,
      lng: 0
    };
    this.formWaterwise.controls["lat"].setValue("");
    this.formWaterwise.controls["lng"].setValue("");
  }

  /**
   * Obtiene la lista de marcas de waterwise para el select del formulario.
   */
  getBrands = (): void => {
    this.loadingBrands = true;
    this.waterwiseBrandsService.listWaterwiseBrands("?page=1&page_size=500").subscribe(
      (resp) => {
        this.loadingBrands = false;
        this.brands = resp.results;
      },
      () => {
        this.loadingBrands = false;
      }
    );
  };

  getValueMetaData = (val) => {
    this.generated_meta_data = val;
  };
}
